// Embedded desktop dashboard: nine tools on page one, Recovery on page two.
import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Settings, ArrowRight, Lock, ChevronLeft, ChevronRight } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { CARDS, GATED_FEATURE } from "./Home";
import "./Workspace.css";

const IS_EXE = typeof window !== "undefined" && window.tiroDesktopApp === true;
// Yamaha Flash Tools is Windows-engine only: hidden in BOTH the Windows EXE and the Android app.
const HIDDEN_IN_EXE = new Set(["/file-service", "/shindengen-seedkey", "/yamaha-flashtools"]);
const ORDER = ["/honda-flashtools", "/yamaha-flashtools", "/mcu-read-write", "/map-editor", "/xdf-adx-toolkit", "/pinout", "/immo-calculator", "/database", "/bin-converter", "/honda-titan-checksum", "/recovery-generator"];
const PER_PAGE = 9;
// Mirror the existing route gates; badges never grant access to a tool.
const WORKSPACE_FEATURE = { ...GATED_FEATURE, "/honda-flashtools": "map-editor", "/mcu-read-write": "mcu-read-write" };

const WorkspaceCard = ({ card, locked }) => {
  const slug = card.path.slice(1);
  const Icon = card.icon;
  return (
    <Link to={card.path} draggable={false} data-testid={`ws-card-${slug}`} className="workspace-card"
      aria-labelledby={`ws-title-${slug}`} aria-describedby={`ws-status-${slug}`}>
      <img src={`/workspace-cards/${slug}.webp`} alt="" draggable={false}
        data-testid={`ws-image-${slug}`} className="workspace-card-art" />
      <div className="workspace-card-shade" aria-hidden="true" />
      <div className="workspace-card-content">
        <div className="workspace-card-top">
          <span className="workspace-card-icon" aria-hidden="true"><Icon size={21} strokeWidth={1.7} /></span>
          <span id={`ws-status-${slug}`} data-testid={`ws-status-${slug}`}
            className={`workspace-card-status${locked ? " is-locked" : card.tagTone === "red" ? " is-security" : ""}`}>
            {locked && <Lock size={10} aria-hidden="true" />}{locked ? "LOCKED" : card.tag || "AVAILABLE"}
          </span>
        </div>
        <h2 id={`ws-title-${slug}`} data-testid={`ws-title-${slug}`} className="workspace-card-title">{card.name}</h2>
        <p data-testid={`ws-description-${slug}`} className="workspace-card-description">{card.desc}</p>
        <span data-testid={`ws-open-${slug}`} className="workspace-card-action">
          {card.cta || "Open Tool"}<ArrowRight size={15} aria-hidden="true" />
        </span>
      </div>
    </Link>
  );
};

export default function Workspace() {
  const { user, isAdmin } = useAuth();
  const [page, setPage] = useState(0);
  const approved = isAdmin || (!!user && user.status === "active" && !user.license_expired);
  const unlocked = (path) => isAdmin || (approved && (!WORKSPACE_FEATURE[path] ||
    (Array.isArray(user?.features) ? user.features.includes(WORKSPACE_FEATURE[path]) : path !== "/mcu-read-write")));
  const cards = CARDS.filter((c) => !(IS_EXE && HIDDEN_IN_EXE.has(c.path)))
    .slice().sort((a, b) => { const ia = ORDER.indexOf(a.path), ib = ORDER.indexOf(b.path); return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib); });
  const pages = Math.max(1, Math.ceil(cards.length / PER_PAGE));
  const shown = cards.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);
  const gesture = useRef(null);
  const suppressClick = useRef(false);
  const prevButton = useRef(null);
  const nextButton = useRef(null);
  const changePage = (next, event) => {
    setPage(next);
    if (event?.detail === 0) requestAnimationFrame(() => (next === pages - 1 ? prevButton : nextButton).current?.focus());
  };
  const onPointerDown = (event) => {
    suppressClick.current = false;
    if (event.isPrimary && event.button === 0) gesture.current = { x: event.clientX, y: event.clientY };
  };
  const onPointerUp = (event) => {
    if (!event.isPrimary || !gesture.current) return;
    const dx = event.clientX - gesture.current.x, dy = event.clientY - gesture.current.y;
    gesture.current = null;
    suppressClick.current = Math.hypot(dx, dy) > 10;
    if (Math.abs(dx) < 60 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
    setPage((p) => Math.min(pages - 1, Math.max(0, p + (dx < 0 ? 1 : -1))));
  };

  return (
    <div className="workspace-page" data-testid="workspace-page" onPointerDown={onPointerDown} onPointerUp={onPointerUp}
      onPointerCancel={() => { gesture.current = null; }}
      onClickCapture={(event) => { if (suppressClick.current) { event.preventDefault(); event.stopPropagation(); suppressClick.current = false; } }}>
      <div className="workspace-heading">
        <div className="workspace-heading-label">
          <Settings className="h-7 w-7 shrink-0 text-slate-200" strokeWidth={1.8} aria-hidden="true" />
          <div>
            <h1 data-testid="workspace-title">ECU Workspace</h1>
            <p data-testid="workspace-subtitle">All essential tools for ECU tuning, flashing, and calibration in one place.</p>
          </div>
        </div>
        {pages > 1 && (
          <nav className="workspace-pager" data-testid="ws-pager" aria-label="Workspace pages">
            <button ref={prevButton} type="button" data-testid="ws-prev" aria-label="Previous page" disabled={page === 0} onClick={(event) => changePage(page - 1, event)}><ChevronLeft size={16} /></button>
            <span data-testid="ws-page-number" aria-live="polite">Page <b>{page + 1}</b> / {pages}</span>
            <button ref={nextButton} type="button" data-testid="ws-next" aria-label="Next page" disabled={page >= pages - 1} onClick={(event) => changePage(page + 1, event)}><ChevronRight size={16} /></button>
          </nav>
        )}
      </div>
      <div className="workspace-grid" data-testid="ws-grid">
        {shown.map((card) => <WorkspaceCard key={card.path} card={card} locked={!!WORKSPACE_FEATURE[card.path] && !unlocked(card.path)} />)}
      </div>
      {pages > 1 && (
        <nav className="workspace-dots" data-testid="ws-dots" aria-label="Jump to workspace page">
          {Array.from({ length: pages }).map((_, i) => (
            <button key={i} type="button" data-testid={`ws-page-${i + 1}`} aria-label={`Go to page ${i + 1}`}
              aria-current={i === page ? "page" : undefined} onClick={() => setPage(i)} />
          ))}
        </nav>
      )}
    </div>
  );
}
